import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { SpeakUpLogo } from "@/components/Layout";
import { toast } from "sonner";

export default function Splash() {
    const nav = useNavigate();
    const { setConsumer, setNeedsConsent } = useAuth();
    const [step, setStep] = useState("phone"); // phone | otp
    const [phone, setPhone] = useState("");
    const [otp, setOtp] = useState("");
    const [devOtp, setDevOtp] = useState("");
    const [loading, setLoading] = useState(false);

    const requestOtp = async (e) => {
        e?.preventDefault();
        const trimmed = phone.trim();
        if (!/^\+?\d{8,15}$/.test(trimmed)) {
            toast.error("Enter a valid phone number with country code (e.g. +91…).");
            return;
        }
        setLoading(true);
        try {
            const r = await api.post("/auth/request-otp", { phone: trimmed });
            setDevOtp(r.data.dev_otp || "");
            setStep("otp");
            toast.success("OTP sent");
        } catch (err) {
            toast.error("Failed to send OTP");
        } finally {
            setLoading(false);
        }
    };

    const verifyOtp = async (e) => {
        e?.preventDefault();
        setLoading(true);
        try {
            const r = await api.post("/auth/verify-otp", { phone: phone.trim(), otp });
            setConsumer(r.data.consumer);
            setNeedsConsent(r.data.needs_consent);
            if (r.data.needs_consent) nav("/consent");
            else nav("/");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Invalid OTP");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="min-h-screen flex flex-col items-center justify-between relative overflow-hidden"
            style={{ background: "linear-gradient(180deg, #FD4C41 0%, #E90B17 100%)" }}
            data-testid="splash-screen"
        >
            {/* Top brand */}
            <div className="w-full max-w-md px-6 pt-14 flex flex-col items-center text-white">
                <SpeakUpLogo size={56} className="mb-4 bg-white/15 p-2" />
                <h1 className="display text-white text-[34px] leading-tight text-center" style={{ color: "white" }}>
                    Speak up.
                    <br />
                    We'll take it up.
                </h1>
                <p className="mt-3 text-white/90 text-[15px] text-center max-w-sm">
                    Raise a banking, NBFC or credit-card grievance by voice — in your language. We draft, route and
                    track it all the way to the RBI Ombudsman if needed.
                </p>
            </div>

            {/* Auth card */}
            <div className="w-full max-w-md px-5 pb-10">
                <div className="speakup-card p-6 speakup-fade-up" data-testid="auth-card">
                    {step === "phone" && (
                        <form onSubmit={requestOtp} className="space-y-4">
                            <div>
                                <label className="block text-sm font-semibold text-[var(--speakup-text-strong)]">
                                    Your phone number
                                </label>
                                <p className="text-xs text-[var(--speakup-text-muted)] mt-1">
                                    We use this to sign you in and store your complaints securely.
                                </p>
                            </div>
                            <input
                                data-testid="splash-phone-input"
                                inputMode="tel"
                                autoFocus
                                placeholder="+91 98765 43210"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                className="w-full h-12 px-4 rounded-[12px] border border-[var(--speakup-border)] bg-white text-[16px] tabular focus:outline-none focus:ring-2 focus:ring-[var(--speakup-red)]/40"
                            />
                            <button
                                type="submit"
                                disabled={loading}
                                data-testid="splash-send-otp-btn"
                                className="w-full h-12 rounded-[12px] text-white font-semibold disabled:opacity-60"
                                style={{ background: "var(--speakup-red)" }}
                            >
                                {loading ? "Sending…" : "Send OTP"}
                            </button>
                            <p className="text-[11px] text-[var(--speakup-text-muted)] leading-relaxed">
                                By continuing you agree to our privacy notice (next screen). We never call you, and we
                                never send email to your bank on our own.
                            </p>
                        </form>
                    )}
                    {step === "otp" && (
                        <form onSubmit={verifyOtp} className="space-y-4">
                            <div>
                                <label className="block text-sm font-semibold text-[var(--speakup-text-strong)]">
                                    Enter the 6-digit code
                                </label>
                                <p className="text-xs text-[var(--speakup-text-muted)] mt-1">
                                    Sent to <span className="tabular">{phone}</span>.{" "}
                                    <button
                                        type="button"
                                        onClick={() => setStep("phone")}
                                        className="underline"
                                        data-testid="splash-change-phone"
                                    >
                                        Change
                                    </button>
                                </p>
                            </div>
                            <input
                                data-testid="splash-otp-input"
                                inputMode="numeric"
                                autoFocus
                                maxLength={6}
                                placeholder="••••••"
                                value={otp}
                                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                                className="w-full h-12 px-4 rounded-[12px] border border-[var(--speakup-border)] text-[20px] tabular tracking-[6px] text-center focus:outline-none focus:ring-2 focus:ring-[var(--speakup-red)]/40"
                            />
                            {devOtp && (
                                <div
                                    className="text-xs px-3 py-2 rounded-[8px] bg-amber-50 border border-amber-200 text-amber-900"
                                    data-testid="dev-otp-hint"
                                >
                                    Dev: OTP is <span className="tabular font-semibold">{devOtp}</span> (real SMS
                                    via MSG91 wires later).
                                </div>
                            )}
                            <button
                                type="submit"
                                disabled={loading || otp.length < 4}
                                data-testid="splash-verify-otp-btn"
                                className="w-full h-12 rounded-[12px] text-white font-semibold disabled:opacity-60"
                                style={{ background: "var(--speakup-red)" }}
                            >
                                {loading ? "Verifying…" : "Verify & continue"}
                            </button>
                        </form>
                    )}
                </div>
                <p className="text-center text-white/80 text-xs mt-4">For RBI-regulated institutions in India.</p>
            </div>
        </div>
    );
}
